import { useState, useEffect } from 'react';
import { BrowserRouter, useLocation } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { PWAInstall } from "./components/PWAInstall";
import Sidebar from "./components/feature/Sidebar";
import { BottomNav } from "./components/feature/BottomNav";
import { PWAInstallGuide } from "./components/base/PWAInstallGuide";
import { ReloadPrompt } from "./components/base/ReloadPrompt";
import { SyncManager } from "./services/SyncManager";
import { Toast } from "./components/base/Toast";
import { ErrorBoundary } from "./components/base/ErrorBoundary";

function AppContent({ mobileMenuOpen, setMobileMenuOpen }: any) {
  const [user, setUser] = useState<any>(null);
  const [syncStatus, setSyncStatus] = useState<'SYNCING' | 'IDLE' | 'ERROR'>('IDLE');
  const location = useLocation();

  useEffect(() => {
    const checkUser = () => {
      const stored = localStorage.getItem('atelier_bassoum_user');
      if (stored) {
        try { setUser(JSON.parse(stored)); } catch (e) { setUser(null); }
      } else {
        setUser(null);
      }
    };
    checkUser();
    window.addEventListener('storage', checkUser);
    return () => window.removeEventListener('storage', checkUser);
  }, []);

  useEffect(() => {
    try {
      const syncManager = new SyncManager((status) => setSyncStatus(status));
      syncManager.sync().catch(err => console.error("Sync error:", err));
    } catch (err) {
      console.error("Failed to initialize SyncManager:", err);
    }
  }, []);

  // On garde la navigation visible par défaut pour tous les utilisateurs connectés
  useEffect(() => {
      // Fermer le menu mobile lors d'un changement de route
      setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  return (
    <>
      <AppRoutes />
      
      {/* Mobile Sidebar Overlay */}
      {user && mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[100] flex">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="relative w-64 bg-foret-600 shadow-2xl animate-in slide-in-from-left duration-300">
             <Sidebar role={user.role} nom={user.nom} onToggle={() => setMobileMenuOpen(false)} />
          </div>
        </div>
      )}

      {user && <BottomNav role={user.role} />}
      <PWAInstall />
      <PWAInstallGuide />
      <ReloadPrompt />
      
      {syncStatus === 'SYNCING' && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[300]">
          <div className="bg-foret-600 text-white px-4 py-2 rounded-full shadow-2xl flex items-center gap-3 animate-bounce">
            <i className="ri-refresh-line animate-spin" />
            <span className="text-xs font-black uppercase tracking-widest">Synchronisation...</span>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    (window as any).toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);
    (window as any).closeMobileMenu = () => setMobileMenuOpen(false);

    return () => {
      delete (window as any).toggleMobileMenu;
      delete (window as any).closeMobileMenu;
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <BrowserRouter basename={__BASE_PATH__}>
          <AppContent mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        </BrowserRouter>
      </ErrorBoundary>
    </I18nextProvider>
  );
}

export default App;
